// Live scorecard chart — multi-series lines from host metric samples + target guides.
import { useClient } from '@cortex/react-query';
import type { TrialScorecardFact } from '@cortex/types';
import { useQuery } from '@tanstack/react-query';
import { useMemo, type CSSProperties } from 'react';

interface IMetricSampleRow {
  id: string;
  laneId: string;
  missionId: string | null;
  value: number;
  satisfied: boolean;
  createdAt: string;
}

const palette = [
  '#3b82f6', // blue
  '#10b981', // emerald
  '#f59e0b', // amber
  '#a855f7', // purple
  '#ef4444', // red
  '#06b6d4', // cyan
  '#f97316', // orange
  '#84cc16', // lime
];

const chartW = 560;
const chartH = 180;
const padL = 44;
const padR = 12;
const padT = 14;
const padB = 28;

function metricIdFromLane(laneId: string): string | null {
  return laneId.startsWith('metric:') ? laneId.slice('metric:'.length) : null;
}

function niceExtent(values: number[]): { min: number; max: number } {
  if (values.length === 0) return { min: 0, max: 1 };
  let min = Math.min(...values);
  let max = Math.max(...values);
  if (min === max) {
    const pad = Math.abs(min) * 0.1 || 1;
    min -= pad;
    max += pad;
  } else {
    const span = max - min;
    min -= span * 0.08;
    max += span * 0.12;
  }
  return { min, max };
}

function formatTick(n: number): string {
  if (!Number.isFinite(n)) return '—';
  const abs = Math.abs(n);
  if (abs >= 1000) return n.toFixed(0);
  if (abs >= 10) return n.toFixed(1);
  if (abs >= 1) return n.toFixed(2);
  return n.toPrecision(2);
}

function formatTime(iso: string): string {
  try {
    return new Date(iso).toLocaleTimeString(undefined, {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
  } catch {
    return '';
  }
}

export function ScorecardChart({
  agentId,
  threadId,
  board,
}: {
  agentId: string;
  threadId: string;
  board: TrialScorecardFact;
}) {
  const client = useClient();
  const base = `v1/persistent-agent-groups/${agentId}/agents/${threadId}`;
  const runId = board.runId;

  const samplesQ = useQuery({
    queryKey: ['pa-scorecard-samples', threadId, runId],
    queryFn: () =>
      client.http
        .get(`${base}/metric-samples`, {
          searchParams: {
            limit: '400',
            ...(runId ? { missionId: runId } : {}),
          },
        })
        .json<IMetricSampleRow[]>(),
    enabled: Boolean(runId),
    refetchInterval: board.status === 'active' ? 4_000 : false,
  });

  const series = useMemo(() => {
    const rows = samplesQ.data ?? [];
    const byMetric = new Map<string, IMetricSampleRow[]>();
    for (const r of rows) {
      const mid = metricIdFromLane(r.laneId);
      if (!mid) continue;
      const list = byMetric.get(mid) ?? [];
      list.push(r);
      byMetric.set(mid, list);
    }

    return board.metrics.map((m, i) => {
      const pts = (byMetric.get(m.id) ?? []).slice().sort((a, b) => {
        return new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
      });
      const target =
        m.baselineMode === 'delta' && m.baselineValue != null
          ? m.baselineValue + m.target
          : m.target;
      return {
        id: m.id,
        label: m.label ?? m.id,
        color: palette[i % palette.length] ?? '#3b82f6',
        target,
        comparator: m.comparator,
        lineStatus: m.lineStatus,
        lastValue: m.lastValue ?? null,
        points: pts.map((p) => ({
          t: new Date(p.createdAt).getTime(),
          v: p.value,
          ok: p.satisfied,
        })),
      };
    });
  }, [board.metrics, samplesQ.data]);

  const geometry = useMemo(() => {
    const allVals: number[] = [];
    const allTs: number[] = [];
    for (const s of series) {
      for (const p of s.points) {
        allVals.push(p.v);
        allTs.push(p.t);
      }
      if (Number.isFinite(s.target)) allVals.push(s.target);
      if (s.lastValue != null) allVals.push(s.lastValue);
    }
    const { min: yMin, max: yMax } = niceExtent(allVals);
    const tMin = allTs.length ? Math.min(...allTs) : Date.now() - 60_000;
    const tMax = allTs.length ? Math.max(...allTs) : Date.now();
    const tSpan = Math.max(tMax - tMin, 1_000);
    const ySpan = Math.max(yMax - yMin, 1e-9);
    const innerW = chartW - padL - padR;
    const innerH = chartH - padT - padB;

    const xOf = (t: number) => padL + ((t - tMin) / tSpan) * innerW;
    const yOf = (v: number) => padT + (1 - (v - yMin) / ySpan) * innerH;

    const yTicks = [0, 0.25, 0.5, 0.75, 1].map((f) => yMin + f * ySpan);
    const xTicks = [0, 0.5, 1].map((f) => tMin + f * tSpan);

    return { yMin, yMax, tMin, tMax, xOf, yOf, yTicks, xTicks, innerW, innerH };
  }, [series]);

  const wrap: CSSProperties = {
    marginTop: 10,
    border: '1px solid var(--border, #2a2a2a)',
    borderRadius: 8,
    padding: '10px 12px 8px',
    background: 'var(--surface-2, rgba(255,255,255,0.02))',
  };

  if (!runId) return null;

  const hasPoints = series.some((s) => s.points.length > 0);
  if (samplesQ.isLoading && !hasPoints) {
    return (
      <div style={wrap}>
        <div className="t-xs t-muted">Loading scorecard chart…</div>
      </div>
    );
  }

  if (!hasPoints) {
    return (
      <div style={wrap}>
        <div className="t-xs t-muted">
          Chart arms after the first sense-hub samples land (a few poll intervals).
        </div>
      </div>
    );
  }

  return (
    <div style={wrap}>
      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 10,
          marginBottom: 8,
          alignItems: 'center',
        }}
      >
        <strong className="t-xs" style={{ letterSpacing: 0.3 }}>
          Live metrics
        </strong>
        {series.map((s) => (
          <span
            key={s.id}
            className="t-xs"
            style={{ display: 'inline-flex', gap: 5, alignItems: 'center' }}
          >
            <span
              style={{
                width: 8,
                height: 8,
                borderRadius: 999,
                background: s.color,
                boxShadow: ['pass', 'achieved', 'healthy'].includes(s.lineStatus)
                  ? `0 0 0 2px ${s.color}55`
                  : undefined,
              }}
            />
            <span style={{ color: 'var(--text-secondary)' }}>{s.label}</span>
            <span style={{ fontVariantNumeric: 'tabular-nums' }}>
              {s.lastValue == null ? '—' : formatTick(s.lastValue)}
            </span>
            <span className="t-muted">
              {s.comparator} {formatTick(s.target)}
            </span>
          </span>
        ))}
      </div>

      <svg
        viewBox={`0 0 ${chartW} ${chartH}`}
        width="100%"
        height={chartH}
        role="img"
        aria-label="Scorecard metric time series"
        style={{ display: 'block' }}
      >
        {/* grid */}
        {geometry.yTicks.map((v, i) => {
          const y = geometry.yOf(v);
          return (
            <g key={`yg-${i}`}>
              <line
                x1={padL}
                x2={chartW - padR}
                y1={y}
                y2={y}
                stroke="var(--border, #333)"
                strokeWidth={1}
                strokeDasharray={i === 0 || i === geometry.yTicks.length - 1 ? undefined : '3 4'}
                opacity={0.7}
              />
              <text
                x={padL - 6}
                y={y + 3}
                textAnchor="end"
                fontSize={9}
                fill="var(--text-muted, #888)"
              >
                {formatTick(v)}
              </text>
            </g>
          );
        })}

        {geometry.xTicks.map((t, i) => (
          <text
            key={`xt-${i}`}
            x={geometry.xOf(t)}
            y={chartH - 8}
            textAnchor={i === 0 ? 'start' : i === geometry.xTicks.length - 1 ? 'end' : 'middle'}
            fontSize={9}
            fill="var(--text-muted, #888)"
          >
            {formatTime(new Date(t).toISOString())}
          </text>
        ))}

        {/* target guides */}
        {series.map((s) => {
          const y = geometry.yOf(s.target);
          return (
            <line
              key={`tgt-${s.id}`}
              x1={padL}
              x2={chartW - padR}
              y1={y}
              y2={y}
              stroke={s.color}
              strokeWidth={1}
              strokeDasharray="5 4"
              opacity={0.45}
            />
          );
        })}

        {/* series paths + dots */}
        {series.map((s) => {
          if (s.points.length === 0) return null;
          const d = s.points
            .map((p, i) => {
              const x = geometry.xOf(p.t);
              const y = geometry.yOf(p.v);
              return `${i === 0 ? 'M' : 'L'}${x.toFixed(1)},${y.toFixed(1)}`;
            })
            .join(' ');
          return (
            <g key={`s-${s.id}`}>
              <path
                d={d}
                fill="none"
                stroke={s.color}
                strokeWidth={2}
                strokeLinejoin="round"
                strokeLinecap="round"
              />
              {s.points.map((p, i) => {
                const x = geometry.xOf(p.t);
                const y = geometry.yOf(p.v);
                return (
                  <circle
                    key={i}
                    cx={x}
                    cy={y}
                    r={p.ok ? 3.2 : 2.4}
                    fill={p.ok ? s.color : 'var(--surface, #111)'}
                    stroke={s.color}
                    strokeWidth={1.5}
                  >
                    <title>
                      {s.label}: {formatTick(p.v)} @ {formatTime(new Date(p.t).toISOString())}
                      {p.ok ? ' · satisfied' : ''}
                    </title>
                  </circle>
                );
              })}
            </g>
          );
        })}
      </svg>

      <div className="t-xs t-muted" style={{ marginTop: 2 }}>
        Dashed = target · filled dots = sample met the line · refreshes while active
      </div>
    </div>
  );
}
